import { useQuery } from '@apollo/client';
import { useFormik } from 'formik';
import { Duration } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object, string } from 'yup';
import { applyServerErrors } from '~/api/helpers';
import { useUpdatePlaylistTags } from '~/api/playlist-tags';
import { useUpdatePlaylistSettings } from '~/api/playlists';
import { DetailTextArea } from '~/components/forms/details';
import { NumberInput } from '~/components/inputs';
import { TagsAutocomplete } from '~/components/inputs/TagsAutocomplete/TagsAutocomplete';
import { useAppContext } from '~/contexts';
import { PlaylistSettingsDocument } from '../../queries/settings.generated';
import { usePlaylist } from '../context';

const validationSchema = object({
  description: string(),
  defaultImageDuration: number()
    .nullable()
    .optional()
    .min(5)
    .max(60 * 60),
});

export const useSettingsForm = () => {
  const { t } = useTranslation();
  const { playlist, playlistTags } = usePlaylist();
  const { currentNetwork } = useAppContext();
  const [updateTags] = useUpdatePlaylistTags();
  const [updatePlaylist] = useUpdatePlaylistSettings();

  const { data } = useQuery(PlaylistSettingsDocument, {
    variables: {
      networkId: currentNetwork.id,
      playlistId: playlist.id,
    },
  });

  const onHandleTagChange = useCallback(
    async (taggableId: number, tags: string[]) => {
      try {
        await updateTags({
          variables: {
            taggableId,
            tags,
          },
        });
      } catch (error) {
        console.error('Failed to update tags:', error);
      }
    },
    [updateTags],
  );

  const handleTagChange = useCallback(
    async (_event: React.SyntheticEvent, newValue: string[]) => {
      try {
        await onHandleTagChange(playlist.id, newValue);
      } catch (error) {
        console.error('Failed to handle tag change:', error);
      }
    },
    [onHandleTagChange, playlist],
  );

  const defaultImageDuration = data?.network?.playlist?.defaultImageDuration ?? null;

  const initialValues = useMemo(
    () => ({
      defaultImageDuration: defaultImageDuration
        ? Duration.fromISO(defaultImageDuration).as('seconds')
        : null,
      description: data?.network?.playlist?.description || '',
    }),
    [defaultImageDuration, data?.network?.playlist?.description],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const newValues = validationSchema.cast(values);

      const durationSeconds: number | undefined = newValues.defaultImageDuration ?? undefined;

      const defaultImageDuration =
        durationSeconds !== undefined
          ? Duration.fromObject({ seconds: durationSeconds }).toISO()
          : null;

      const result = await updatePlaylist({
        variables: {
          playlistId: playlist.id,
          patch: {
            description: newValues.description,
            defaultImageDuration,
          },
        },
      });
      applyServerErrors(result, setErrors);
    },
    validateOnMount: true,
    validationSchema,
  });

  const generalSettings = useMemo(() => {
    const defaultNetworkDuration = data?.network?.settings.defaultImageDuration
      ? Duration.fromISO(data.network.settings.defaultImageDuration).as('seconds')
      : '';

    return [
      {
        heading: t('pages.playlists.detail.settings.fields.default_image_duration.heading'),
        subHeading: t('pages.playlists.detail.settings.fields.default_image_duration.subheading'),
        dataField: (
          <NumberInput
            disabled={formik.isSubmitting}
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('defaultImageDuration', val)}
            value={formik.values.defaultImageDuration}
            helperText={t(
              'pages.playlists.detail.settings.fields.default_image_duration.helper_text',
              { defaultDuration: defaultNetworkDuration },
            )}
            min={5}
            max={60 * 60}
          />
        ),
      },
      {
        heading: t('common.labels.description'),
        subHeading: t('pages.playlists.detail.settings.fields.description.subheading'),
        dataField: (
          <DetailTextArea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
            maxRows={4}
          />
        ),
      },
      {
        heading: t('common.labels.tags'),
        subHeading: t('pages.playlists.detail.settings.fields.tags.subheading'),
        dataField: (
          <TagsAutocomplete
            options={playlistTags}
            value={Array.from(playlist.tags)}
            onChange={handleTagChange}
          />
        ),
      },
    ];
  }, [
    playlist,
    playlistTags,
    handleTagChange,
    formik,
    data?.network?.settings.defaultImageDuration,
    t,
  ]);

  return {
    generalSettings,
    formik,
  };
};
